"use client";

import posthog from "posthog-js";
import type { Properties } from "posthog-js";
import type { PaymentMethod } from "@/types";
import { hasAnalyticsConsent } from "@/lib/consent";
import { isInternalVisitor } from "@/lib/internal-visitor";

// Thin wrapper around posthog-js. Every other module talks to analytics
// through this file only (never `posthog-js` directly) so the vendor stays
// swappable, and the two guards below ("not configured" and "not consented
// yet") live in exactly one place.
//
// Consent flow (two tiers, see cookieless_mode in initAnalytics()):
//
// - Tier 1 (anonymous): a visitor who has never touched the cookie banner,
//   and a visitor who clicked 거부, both capture pageviews/autocapture/our
//   custom events under a fixed, non-identifying distinct_id
//   ("$posthog_cookieless") with NO cookie or localStorage write and NO
//   session replay. This is cookieless_mode: "on_reject" combined with
//   opt_out_capturing_by_default: true, see the comment on cookieless_mode
//   below for exactly how those two combine.
// - Tier 2 (full): a visitor who clicked 동의 gets today's full behavior —
//   persistent distinct_id, normal cookies/localStorage, session replay.
//
// grantAnalyticsConsent() (called on 동의, and eagerly on load for a
// returning visitor who already accepted, see the bottom of this file)
// switches a visitor from tier 1 to tier 2 immediately, no reload needed.
// revokeAnalyticsConsent() (called on 거부) explicitly clears any
// previously stored PostHog data via posthog.reset(true) before opting
// out, then falls back to tier 1 rather than going silent. posthog-js
// also does its own internal reset when transitioning out of an accepted
// state under cookieless_mode: "on_reject" (verified in
// node_modules/posthog-js/dist/main.js, opt_out_capturing()), but we keep
// our explicit reset(true) as the deterministic, always-run version of
// that instead of relying on that internal branch alone.

// One entry per event this app actually emits, with its exact prop shape,
// so track() rejects a typo'd event name or a missing/mistyped prop at
// compile time instead of silently sending nothing useful to PostHog.
type AnalyticsEvent =
  | { name: "product_viewed"; props: { product_name: string; price: number } }
  | {
      name: "order_started";
      props: {
        // 장바구니 다중 상품 주문을 지원하면서 product_name(단일 문자열)을
        // product_names(배열)로 바꿨다. PostHog에 이 이벤트를 참조하는 기존
        // 저장된 인사이트/필터가 있다면 함께 갱신해야 한다.
        product_names: string[];
        item_count: number;
        total_quantity: number;
        amount: number;
      };
    }
  | { name: "payment_method_selected"; props: { method: PaymentMethod } }
  | {
      name: "payment_attempted";
      props: { method: PaymentMethod; amount: number };
    }
  | {
      name: "order_failed";
      props: {
        method: PaymentMethod;
        // 항상 안정적인 짧은 코드. PG가 돌려준 자유 형식 메시지는 여기 절대
        // 넣지 않는다(그룹핑 프로퍼티의 카디널리티가 무한정 커짐).
        reason: string;
        // PG 메시지 등 참고용 자유 형식 텍스트. 그룹핑에는 절대 쓰지 않는다.
        detail?: string;
      };
    }
  | {
      name: "order_completed";
      props: {
        // Callers (OrderCompletedTracker) always pass the real order
        // number here — it's a handle to look up one specific customer's
        // order, so track() itself replaces it with null in tier 1 (no
        // explicit 동의 yet), see anonymizeOrderCompletedProps() below.
        order_number: string;
        amount: number;
        method: PaymentMethod;
        // order_started와 같은 이유로 product_name(단일 문자열)을
        // product_names(배열) + item_count로 바꿨다.
        product_names: string[];
        item_count: number;
        is_paid: boolean;
        // PG 승인 응답의 결제 상세. 무통장입금 등 PG를 거치지 않은 결제,
        // 또는 승인 응답에 값이 없던 경우는 null이다. 항상 명시적으로 null을
        // 보내고 프로퍼티 자체를 생략하지는 않는다(PostHog 필터가
        // "설정 안 됨"과 "명시적으로 없음"을 헷갈리지 않도록).
        payment_detail_type: string | null;
        card_issuer: string | null;
        installment_months: number | null;
      };
    };

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;

let initialized = false;

function isConfigured(): boolean {
  return !!POSTHOG_KEY && !!POSTHOG_HOST;
}

// Query-string params that must never reach PostHog. KakaoPay appends
// pg_token, and other PGs append their own result parameters when sending
// the customer's browser back to /order/complete after payment, and our own
// success URLs there already carry orderId. orderNumber is the same class
// of problem one hop over: the order-confirmation email links to
// /order/lookup?orderNumber=... (src/lib/email.ts), and that page is
// public and tracked. Values are redacted in place (not dropped), so the
// URL/path stays usable for funnel analysis.
const SENSITIVE_URL_PARAMS = [
  "pg_token",
  "paymentKey",
  "orderId",
  "amount",
  "orderNumber",
];

function redactSensitiveUrlParams(value: string): string {
  return SENSITIVE_URL_PARAMS.reduce(
    (result, param) =>
      result.replace(new RegExp(`([?&]${param}=)[^&#]*`, "g"), "$1redacted"),
    value,
  );
}

// True only for a plain `{}`-style object (object literal, or
// Object.create(null)), never for a Date/RegExp/Map/Set/class instance.
// Object.fromEntries(Object.entries(...)) silently destroys anything in
// the latter group into a bare `{}` (a Date loses its time value
// entirely), so redactValueDeep must check this before it decides to
// rebuild an object rather than leave it alone.
function isPlainObject(value: object): boolean {
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

// `seen` tracks objects/arrays currently on THIS call's ancestor chain (add
// before recursing into children, remove after), not every object visited
// overall. That distinction matters: two unrelated properties that happen
// to point at the same shared object are not a cycle and must both still
// be redacted, but an object nested inside its own descendants is a real
// cycle and must not be walked again. On a real cycle we replace the
// repeated reference with the string "[Circular]" rather than returning it
// untouched: leaving the actual circular reference in the returned tree
// would just move the crash downstream, since posthog-js JSON.stringifies
// the outgoing request body, which throws on a circular structure exactly
// like the recursion here would.
function redactValueDeep(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (typeof value === "string") return redactSensitiveUrlParams(value);
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";

  if (Array.isArray(value)) {
    seen.add(value);
    const redacted = value.map((entry) => redactValueDeep(entry, seen));
    seen.delete(value);
    return redacted;
  }

  if (!isPlainObject(value)) {
    // Date, RegExp, Map, Set, DOM nodes, class instances, etc. None of
    // these can carry a redactable query-string value the way a plain
    // object or array can, and rebuilding them via
    // Object.fromEntries(Object.entries(...)) would silently wipe them out
    // (e.g. a Date becomes {}), so leave them exactly as they are.
    return value;
  }

  seen.add(value);
  const redacted = Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      redactValueDeep(entry, seen),
    ]),
  );
  seen.delete(value);
  return redacted;
}

// posthog-js attaches $current_url (and $referrer/$initial_current_url/
// $session_entry_url, which all derive from it) to every captured event,
// not just $pageview. That includes our own order_completed/order_failed
// calls fired from /order/complete, and any $autocapture click there.
// Walking every property of every event and redacting the same params
// catches all of those uniformly, on top of the URL-specific masking
// configured below.
function redactUrlPropertiesDeep(value: Properties): Properties {
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, redactValueDeep(entry)]),
  );
}

export function initAnalytics() {
  if (initialized) return;
  // Internal (developer/farmer) visitor: never call posthog.init() at
  // all, so no event can ever be queued, let alone sent. See the
  // file-level comment in internal-visitor.ts for the ordering guarantee
  // that makes this check see an ?internal=1 flag set on this very page
  // load, not just on a later one.
  if (isInternalVisitor()) return;
  // Re-read into locals so TypeScript narrows them to `string` below.
  // The module-level consts stay `string | undefined`, which would
  // otherwise force a non-null assertion on posthog.init(key, ...).
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  if (!key || !host) return;

  try {
    posthog.init(key, {
      api_host: host,
      // '2026-05-30' is a deliberate pin, not simply "whatever is newest."
      // The ConfigDefaults union (node_modules/@posthog/types/dist/
      // posthog-config.d.ts:370) also includes a newer '2026-06-25', but
      // that baseline additionally turns on
      // session_recording.streamNetworkBody, which we do not want on by
      // default. At '2026-05-30' this sets capture_pageview to
      // 'history_change' (needed since Next.js App Router navigation uses
      // the History API, not full page loads), plus rageclick/
      // session_recording quality defaults (strictMinimumDuration,
      // canvasCapture), replicated explicitly below because passing our
      // own `session_recording` object replaces theirs rather than
      // merging with it.
      defaults: "2026-05-30",
      // Gives tier-1 visitors (see the file-level comment above) anonymous,
      // cookieless capture instead of no capture at all. Verified against
      // the installed declarations (node_modules/@posthog/types/dist/
      // posthog-config.d.ts: cookieless_mode) and the shipped source
      // (node_modules/posthog-js/dist/main.js):
      // - 'always' turns on cookieless mode unconditionally, for sites with
      //   no cookie banner at all. Not what we want, since 동의 must still
      //   unlock full persistence + replay.
      // - 'on_reject' is the one we use. is_capturing() under this mode is
      //   `consent.isRejected() || consent.isOptedIn()` — capture is on
      //   unless the consent state is neither, which can't happen. The
      //   part that makes tier 1 include a visitor who has never touched
      //   the banner (not just one who explicitly clicked 거부) is
      //   isRejected(): `consent === 0 (explicit 거부 stored) ||
      //   (consent === -1 (nothing stored yet) && opt_out_capturing_by_default)`.
      //   With opt_out_capturing_by_default: true below, "never decided"
      //   and "explicitly declined" both satisfy isRejected(), so both
      //   capture anonymously; only consent === 1 (동의) makes isRejected()
      //   false and switches to full persistence.
      // Whether an event is actually captured cookielessly (fixed
      // "$posthog_cookieless" distinct_id, $device_id: null, no
      // cookie/localStorage write) vs. with full persistence is decided
      // per-event by the same isRejected() check. Session replay is gated
      // separately on consent.isOptedOut() (false only once 동의 is given),
      // so it never runs pre-consent regardless of this setting — see
      // session_recording below.
      //
      // This also requires "Cookieless server hash mode" to be turned on
      // in the PostHog project's own settings (Project settings, not this
      // file): posthog-js's own docs warn that without it, PostHog ignores
      // every event sent under cookieless_mode server-side. Confirmed via
      // the PostHog MCP (project 520641, 현들팜):
      // cookieless_server_hash_mode is 2 ("Stateful"), so tier-1 events are
      // accepted. It read 0 ("Disabled") when this was written, which
      // dropped every tier-1 event, so if tier-1 traffic ever goes missing
      // again this setting is the first thing to check.
      cookieless_mode: "on_reject",
      opt_out_capturing_by_default: true,
      // opt_out_capturing_by_default only gates event CAPTURE. Persistence
      // (cookies/localStorage) is a separate flag, default false, so
      // without this a visitor who never touches the banner still gets a
      // distinct_id cookie and localStorage entries written. Verified in
      // the installed declarations (node_modules/@posthog/types/dist/
      // posthog-config.d.ts: opt_out_persistence_by_default, "Determines
      // if users should be opted out of browser data storage... by
      // default"). Technically redundant now that cookieless_mode:
      // "on_reject" is set above — the persistence-disabled check in the
      // shipped source (node_modules/posthog-js/dist/main.js) already
      // forces persistence off whenever cookieless_mode === "on_reject"
      // and the visitor is opted out, regardless of this flag. Left in as
      // a harmless backstop for any future config where cookieless_mode
      // isn't set (e.g. a rollback), same defense-in-depth as the rest of
      // this file.
      opt_out_persistence_by_default: true,
      // Explicit even though it's already posthog-js's own default.
      // This is privacy-relevant and we never call identify(), so it
      // should never be inferred silently.
      person_profiles: "identified_only",
      // Masks SENSITIVE_URL_PARAMS inside $current_url/$referrer (and
      // $initial_current_url/$session_entry_url, both derived from
      // $current_url) at the source, before those properties are even
      // built. Verified in the installed declarations
      // (node_modules/@posthog/types/dist/posthog-config.d.ts): this is
      // the current, non-deprecated mechanism for masking specific query
      // params in the captured URL ("Mask personal data properties from
      // the current URL... you can also add custom properties to mask
      // with custom_personal_data_properties"). mask_personal_data_properties
      // must be true for custom_personal_data_properties to take effect.
      mask_personal_data_properties: true,
      custom_personal_data_properties: SENSITIVE_URL_PARAMS,
      // Backstop for every other captured event and property.
      // sanitize_properties is explicitly marked
      // `@deprecated - use before_send instead` in the same declarations
      // file, so before_send (not sanitize_properties) is the current,
      // correct hook here. Runs on every captured event (custom events,
      // automatic $pageview, $autocapture) and redacts the same params
      // wherever they show up, not just on the URL-shaped properties
      // mask_personal_data_properties already covers.
      before_send: (capture) => {
        if (!capture) return capture;
        // The installed CaptureResult type says `properties` is always a
        // Properties object, but posthog-js's own internals can hand this
        // function an event where it is undefined or null (reproduced
        // directly against realistic capture shapes). There is nothing to
        // redact in that case, so return the capture exactly as received
        // instead of calling Object.entries(undefined), which throws.
        if (!capture.properties) return capture;
        try {
          return {
            ...capture,
            properties: redactUrlPropertiesDeep(capture.properties),
          };
        } catch {
          // redactValueDeep is written so this should never throw (plain
          // objects/arrays only get rebuilt, everything else is left
          // alone, and a WeakSet breaks cycles instead of recursing into
          // them), but before_send must never let an event through
          // unredacted just because that guarantee turned out to be wrong
          // in some case we didn't anticipate. Returning null/undefined
          // here tells posthog-js to drop the event entirely rather than
          // send it (confirmed in the shipped source,
          // node_modules/posthog-js/dist/main.js: capture() checks the
          // before_send result and returns early without sending when
          // it's falsy) — a lost metric is cheaper than a leaked
          // pg_token/paymentKey.
          return null;
        }
      },
      // 브라우저에서 터진 예외를 모은다.
      //
      // 세션 리플레이가 이미 손님 화면을 남기지만, 리플레이는 찾아보는
      // 도구지 알려주는 도구가 아니다. 무언가 잘못됐다는 것을 먼저 알아야
      // 그 세션을 열어보게 되고, 그때까지는 손님이 전화하지 않는 한 모른다.
      // 예외를 따로 모아 두면 오류 쪽에서 리플레이로 거꾸로 찾아갈 수 있다.
      //
      // capture_console_errors는 켜지 않는다. 기본값이 false이고, 우리
      // console.error는 대부분 이미 복구된 상황을 기록으로 남기는 것이라
      // (lib/observability.ts) 진짜 예외와 섞이면 무엇이 손님을 막았는지
      // 가려낼 수 없다. 여기서는 손으로 잡지 못한 것만 받는다.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
      },
      session_recording: {
        maskAllInputs: true,
        // These three are already posthog-js's session-replay recorder
        // defaults (verified in the shipped bundle,
        // node_modules/posthog-js/dist/posthog-recorder.js: blockClass
        // "ph-no-capture", ignoreClass "ph-ignore-input", maskTextClass
        // "ph-mask"), pinned explicitly so the class names our
        // components rely on (see ph-mask/ph-no-capture usages across the
        // order pages) don't silently change on a future posthog-js
        // upgrade. Note blockClass ("ph-no-capture") only controls
        // session-replay recording. Autocapture reads the same class
        // name independently (its own default, unrelated to this
        // session_recording config), so an element needs the literal
        // "ph-no-capture" class in its JSX to be excluded from
        // autocapture too, not just this init option.
        blockClass: "ph-no-capture",
        ignoreClass: "ph-ignore-input",
        maskTextClass: "ph-mask",
        // Carried over from the '2026-05-30' defaults baseline (see
        // comment above) since our explicit session_recording object
        // would otherwise drop them.
        strictMinimumDuration: true,
        canvasCapture: { resolutionScale: 0.6 },
      },
    });
    // Only mark ourselves initialized once posthog.init() has actually
    // returned without throwing. This used to be set unconditionally
    // before the try block, which meant a throwing init() left
    // `initialized` stuck at true forever: the early `if (initialized)
    // return;` guard above would then silently no-op every future
    // initAnalytics() call (e.g. after a transient failure), and every
    // track()/grantAnalyticsConsent()/revokeAnalyticsConsent() call would
    // keep hitting posthog-js in a state it was never actually
    // initialized into, with nothing surfaced anywhere.
    initialized = true;
  } catch {
    // Analytics must never break the app. A failed SDK init is a lost
    // metric, not a broken storefront. `initialized` stays false here, so
    // a later call can retry instead of being silently stuck.
  }

  // Returning visitor who already accepted: opt back in immediately (tier
  // 2, full persistence + replay) so they're captured that way from this
  // very pageview, not just the next one. Anyone else (never decided, or
  // previously declined) is already capturing anonymously (tier 1) from
  // the cookieless_mode config above, no extra call needed here. Gated on
  // `initialized` so we don't call into a posthog-js instance that failed
  // to initialize.
  if (initialized && hasAnalyticsConsent()) {
    grantAnalyticsConsent();
  }
}

export function grantAnalyticsConsent() {
  if (!isConfigured()) return;
  // Internal visitor: posthog was never initialized (see initAnalytics
  // above), so opting in has nothing to do. Stay a no-op explicitly
  // rather than relying on posthog-js tolerating a call before init().
  if (isInternalVisitor()) return;
  try {
    posthog.opt_in_capturing();
  } catch {
    // see initAnalytics
  }
}

export function revokeAnalyticsConsent() {
  if (!isConfigured()) return;
  // Internal visitor: see grantAnalyticsConsent above, same reasoning.
  if (isInternalVisitor()) return;
  // posthog.reset(true) is what actually clears stored data (distinct_id,
  // session, cached person properties) accumulated during a prior 동의.
  // reset() also wipes the consent flag itself (it calls
  // this.consent.reset() internally, confirmed in the posthog-js source),
  // so it must run BEFORE opt_out_capturing(), not after: otherwise
  // opt_out_capturing()'s consent.optInOut(false), which needs to be the
  // last write, gets wiped back to a blank/undecided consent state
  // instead of staying opted out. posthog-js's own opt_out_capturing()
  // now also does an equivalent internal reset(true) when needed under
  // cookieless_mode: "on_reject" (visitor was previously opted in), but
  // we keep this explicit call so the ordering doesn't depend on
  // remembering that internal branch. After this pair runs, the visitor
  // falls back to tier 1 (anonymous, cookieless capture) rather than
  // going silent, since cookieless_mode: "on_reject" above already covers
  // a declined visitor — see the file-level comment and the
  // cookieless_mode comment in initAnalytics().
  try {
    posthog.reset(true);
  } catch {
    // see initAnalytics
  }
  try {
    posthog.opt_out_capturing();
  } catch {
    // see initAnalytics
  }
}

// Whether posthog-js is currently operating in tier 2 (full persistence,
// explicit 동의) rather than tier 1 (anonymous, cookieless — see the
// file-level comment above). Deliberately asks posthog-js for this
// directly instead of re-reading our own consent.ts localStorage record:
// posthog-js's own opt-in state is the actual thing that decides which
// tier an event is captured under, so this can never disagree with what
// really happened to the event the way two independently-read records
// could drift.
//
// has_opted_in_capturing() wraps consent.isOptedIn() (!isOptedOut()).
// Verified in the installed source (node_modules/posthog-js/dist/main.js):
// under our exact config (cookieless_mode: "on_reject",
// opt_out_capturing_by_default: true), isOptedOut() reduces to exactly
// isRejected() — the same check that decides whether posthog-js tags THIS
// event cookieless (_inCookielessMode(), internally called Yi()) — so
// has_opted_in_capturing() is true if and only if the visitor has
// explicitly clicked 동의, and false for both "never decided" and
// explicit 거부. get_explicit_consent_status() reads the same underlying
// value and would also work, but has_opted_in_capturing() is the one
// that's actually named for this question.
export function hasOptedIntoFullAnalytics(): boolean {
  if (!isConfigured()) return false;
  try {
    return posthog.has_opted_in_capturing();
  } catch {
    return false;
  }
}

// A plain `event === "order_completed"` check inside track() below
// wouldn't narrow `props`'s type there, because `props` is typed
// generically over `E` (Extract<AnalyticsEvent, { name: E }>["props"]),
// which control-flow analysis can't narrow through. A type guard is the
// supported way to do that instead of an `as` cast.
function isOrderCompletedCall(
  event: AnalyticsEvent["name"],
  props: unknown,
): props is Extract<AnalyticsEvent, { name: "order_completed" }>["props"] {
  return event === "order_completed";
}

// order_number is a handle a human could use to look up one specific
// customer's order, unlike the rest of this event's fields (amount,
// product_name, method, is_paid, payment_detail_type, card_issuer,
// installment_months), which are aggregate business facts, not handles to
// a person. A visitor who hasn't explicitly clicked 동의 must not have it
// leave the browser at all, even tagged with the non-persistent
// cookieless identity tier 1 otherwise uses.
function anonymizeOrderCompletedProps(
  props: Extract<AnalyticsEvent, { name: "order_completed" }>["props"],
): Omit<
  Extract<AnalyticsEvent, { name: "order_completed" }>["props"],
  "order_number"
> & { order_number: null } {
  return { ...props, order_number: null };
}

export function track<E extends AnalyticsEvent["name"]>(
  event: E,
  props: Extract<AnalyticsEvent, { name: E }>["props"],
) {
  if (!isConfigured()) return;
  // Internal visitor: see grantAnalyticsConsent above, same reasoning —
  // posthog was never initialized, so there is nothing to capture into.
  if (isInternalVisitor()) return;
  try {
    if (isOrderCompletedCall(event, props) && !hasOptedIntoFullAnalytics()) {
      posthog.capture(event, anonymizeOrderCompletedProps(props));
      return;
    }
    posthog.capture(event, props);
  } catch {
    // see initAnalytics
  }
}

// Called eagerly at module scope (guarded for SSR, where `window` is
// undefined) rather than only from a mounted component's effect. A
// component effect runs too late: React commits child effects before a
// later sibling's, so a page-level track() call (e.g. product_viewed in
// ProductPurchasePanel) could fire before an effect-based init ever ran,
// and posthog-js does not buffer: capture() silently drops events before
// init. Module evaluation happens before any component renders or runs an
// effect, since every client component that calls track()/grant/revoke
// (including CookieBanner, mounted on every public page) statically
// imports this file, so this always runs first.
if (typeof window !== "undefined") {
  initAnalytics();
}
